// Your history as the home page reads it: this device's with your other
// devices' (`otherHistory`, from the account sync) added in. Recent tunes,
// Most played, Top picks and the More like shelves all read these, so a tune
// you played on your phone counts on your laptop too.
//
// Memoized on their inputs, so a component selecting them from the store gets
// the same object until something changed; with nothing from other devices
// they ARE this device's own, the very same objects.

import type { PlaylistTuneRef } from '@/storage/playlists';
import { useStore } from './store';
import { combineHistories, recentsAcrossDevices, type TuneHistory } from './tuneHistory';

let historyMemo: { local: TuneHistory; other: TuneHistory; out: TuneHistory } | null = null;

/** Your play history across your devices. */
export function historyView(local: TuneHistory, other: TuneHistory): TuneHistory {
  if (Object.keys(other).length === 0) return local;
  if (historyMemo?.local === local && historyMemo.other === other) return historyMemo.out;
  const out = combineHistories(local, other);
  historyMemo = { local, other, out };
  return out;
}

let recentsMemo: {
  recents: readonly PlaylistTuneRef[];
  local: TuneHistory;
  other: TuneHistory;
  out: PlaylistTuneRef[];
} | null = null;

/** Your recent tunes across your devices, newest first. */
export function recentsView(
  recents: PlaylistTuneRef[],
  local: TuneHistory,
  other: TuneHistory,
): PlaylistTuneRef[] {
  if (Object.keys(other).length === 0) return recents;
  const memo = recentsMemo;
  if (memo?.recents === recents && memo.local === local && memo.other === other) return memo.out;
  const next = recentsAcrossDevices(recents, local, other);
  // The history moves on every play; the list itself rarely does. Keep the
  // same array while its tunes are the same, so nothing downstream redraws.
  const out =
    memo && memo.out.length === next.length && memo.out.every((ref, i) => ref === next[i])
      ? memo.out
      : next;
  recentsMemo = { recents, local, other, out };
  return out;
}

export function useHistoryView(): TuneHistory {
  return useStore((s) => historyView(s.tuneHistory, s.otherHistory));
}

export function useRecentsView(): PlaylistTuneRef[] {
  return useStore((s) => recentsView(s.recentTunes, s.tuneHistory, s.otherHistory));
}
